import type jsPDF from 'jspdf';
import type { VorankuendigungData } from '../../types';
import { generateVorankuendigungPDF } from '../../services/pdfService';
import { haengeBegleitblattAn } from '../../services/pdf/vorankBegleitblatt';
import { normalisiereAufA4 } from '../../services/pdf/vorankScanNormalisieren';
import {
    fassungsInhalt,
    ladeDokumentBytes,
    legeFassungAn,
    legeVorankDateiAb,
    papierEingang,
    signiertSetzen,
} from '../../services/vorank/vorankUnterschriftService';
import type {
    VorankEinleseAuswahl,
    VorankFassungAngelegt,
    VorankFassungZeile,
} from '../../services/vorank/vorankUnterschriftTypes';
import { dateiAusgeben } from '../../services/dateiAusgabe';
import { describeError } from '../../services/errorMessage';
import {
    vorankBegleitblattLink,
    vorankDigitalAblage,
    vorankDigitalAblageFehler,
    vorankEinleseSchluessel,
    vorankPapierAblage,
    vorankPdfUnterschrift,
    vorankUnterschriebenDateiname,
    vorankUnterschriebenQuelle,
    type VorankBegleitblattErgebnis,
    type VorankBegleitblattPlan,
} from './vorankUnterschriftAnsicht';

/**
 * „Vorankündigung unterschreiben“ — die Abläufe mit Wirkung (Plan §5, §7,
 * §8, §9): Export mit Begleitblatt vorbereiten, unterschriebene PDF erzeugen
 * oder laden, digital Unterschriebenes ablegen, Papier einlesen.
 *
 * Bewusst außerhalb der Komponenten: der React-Compiler gibt eine Komponente
 * mit `try…finally` ganz auf (VorankuendigungView, Plot-Fix 23.10.), und
 * diese Abläufe brauchen ihre eigene Fehlerbehandlung. Die Regeln dazu
 * (Texte, Kennungen, Quelle) stehen getestet in `vorankUnterschriftAnsicht.ts`.
 */

// ── Export (Plan §5) ────────────────────────────────────────────────────

export type VorankExportVorbereitet =
    | {
        art: 'ok';
        /** Vorankündigung + ggf. Begleitblatt — fertig zum Download. */
        doc: jsPDF;
        /** Der Stand, der auf dem Blatt steht — Archiv und Ablage nehmen GENAU ihn. */
        snapshot: VorankuendigungData;
        /** Id des Archiv-Schnappschusses, schon an die Fassung gegeben. */
        archivId: string;
        begleitblatt: VorankBegleitblattErgebnis;
        /** Eine Fassung wurde angelegt oder wiedergefunden — die Karte lädt neu. */
        fassung: VorankFassungAngelegt | null;
    }
    | { art: 'fehler'; fehler: unknown; snapshot: VorankuendigungData; archivId: string };

/**
 * PDF erzeugen, Seitenzahl merken, Fassung anlegen (8 s, `null` bei jedem
 * Fehler), Begleitblatt anhängen. Wirft nie. Scheitert das Begleitblatt,
 * wird das Blatt ohne es neu gesetzt — ein halb angehängtes käme sonst mit.
 * Der Plot ruft das hier nie: er hängt kein Begleitblatt an.
 */
export const bereiteVorankExportVor = async (a: {
    snapshot: VorankuendigungData;
    plan: VorankBegleitblattPlan;
    /** Geladene Fassungen — „Fassung vom …“ und „schon unterschrieben“ einer wiedergefundenen Fassung. */
    fassungen: readonly VorankFassungZeile[] | null;
    kontaktMail: string | null;
    origin: string;
    pathname: string;
}): Promise<VorankExportVorbereitet> => {
    const archivId = crypto.randomUUID();
    const jetzt = new Date().toISOString();
    let doc: jsPDF;
    try {
        doc = generateVorankuendigungPDF(a.snapshot);
    } catch (e) {
        return { art: 'fehler', fehler: e, snapshot: a.snapshot, archivId };
    }
    if (a.plan.art === 'keins') {
        return { art: 'ok', doc, snapshot: a.snapshot, archivId, begleitblatt: { art: 'keins' }, fassung: null };
    }

    // Seitenzahl der Vorankündigung OHNE Begleitblatt — die Fassung merkt sie
    // sich für die Vorauswahl beim Einlesen.
    const seiten = doc.getNumberOfPages();
    let angelegt: VorankFassungAngelegt | null = null;
    if (a.plan.art === 'anlegen' || a.plan.art === 'nur_fassung') {
        try {
            angelegt = await legeFassungAn({
                projectId: a.plan.projektId,
                inhalt: fassungsInhalt(a.snapshot),
                seiten,
                archivEintragId: archivId,
            });
        } catch (e) {
            // legeFassungAn wirft laut Vertrag nie — doppelt hält besser: ohne Link weiter.
            console.warn('[Vorankündigung] Fassung nicht angelegt:', e);
            angelegt = null;
        }
    }
    // Haken aus: die Fassung steht (für den Papierweg), das Blatt geht ohne
    // Begleitblatt hinaus. Gesagt wird nur, was der Bearbeiter wissen muss:
    // die Fassung fehlt, oder dieser Stand ist schon unterschrieben.
    if (a.plan.art === 'nur_fassung') {
        const begleitblatt: VorankBegleitblattErgebnis = !angelegt
            ? { art: 'keine_fassung' }
            : angelegt.unterschrieben
                ? { art: 'schon_unterschrieben', fassungVom: angelegt.fassungVom || jetzt }
                : { art: 'keins' };
        return { art: 'ok', doc, snapshot: a.snapshot, archivId, begleitblatt, fassung: angelegt };
    }
    const fassungId = angelegt?.fassungId ?? null;
    const bekannt = fassungId && a.fassungen ? a.fassungen.find(f => f.id === fassungId) ?? null : null;
    const link = vorankBegleitblattLink({ angelegt, bekannt, origin: a.origin, pathname: a.pathname, jetzt });
    if (link.art === 'schon_unterschrieben') {
        return {
            art: 'ok', doc, snapshot: a.snapshot, archivId,
            begleitblatt: { art: 'schon_unterschrieben', fassungVom: link.fassungVom }, fassung: angelegt,
        };
    }

    try {
        await haengeBegleitblattAn(doc, {
            link: link.art === 'link' ? link.link : null,
            data: a.snapshot,
            kontaktMail: a.kontaktMail,
            fassungVom: link.fassungVom,
        });
    } catch (e) {
        console.error('[Vorankündigung] Begleitblatt Error', e);
        try {
            doc = generateVorankuendigungPDF(a.snapshot);
        } catch (e2) {
            return { art: 'fehler', fehler: e2, snapshot: a.snapshot, archivId };
        }
        return {
            art: 'ok', doc, snapshot: a.snapshot, archivId,
            begleitblatt: { art: 'fehler', detail: describeError(e) }, fassung: angelegt,
        };
    }

    let begleitblatt: VorankBegleitblattErgebnis;
    if (link.art === 'link' && angelegt) {
        begleitblatt = { art: 'mit_link', gueltigBis: angelegt.gueltigBis, neu: angelegt.neu };
    } else if (link.art === 'ohne_link' && link.abgelaufenAm) {
        begleitblatt = { art: 'ohne_link', grund: 'abgelaufen', abgelaufenAm: link.abgelaufenAm };
    } else {
        begleitblatt = { art: 'ohne_link', grund: a.plan.art === 'ohne_projekt' ? 'kein_projekt' : 'nicht_verfuegbar' };
    }
    return { art: 'ok', doc, snapshot: a.snapshot, archivId, begleitblatt, fassung: angelegt };
};

// ── Unterschriebene PDF (Plan §7, §9) ───────────────────────────────────

/**
 * Die unterschriebene A4-PDF einer Fassung, ohne Begleitblatt — Quelle für
 * Plot und Behörde. Digital: aus Inhalt + Unterschrift erzeugt. Papier: die
 * normalisierte PDF aus „Dokumente“. Wirft mit Klartext.
 */
export const unterschriebenePdfBytes = async (f: VorankFassungZeile): Promise<ArrayBuffer> => {
    const quelle = vorankUnterschriebenQuelle(f);
    if (!quelle) throw new Error('Zu dieser Fassung liegt keine unterschriebene PDF vor.');
    if (quelle.art === 'dokument') return ladeDokumentBytes(quelle.dokumentId);
    return generateVorankuendigungPDF(f.inhalt, { unterschrift: quelle.unterschrift }).output('arraybuffer');
};

/** „Unterschriebene PDF herunterladen“ — für die Behörde. Wirft mit Klartext. */
export const ladeUnterschriebenePdfHerunter = async (f: VorankFassungZeile): Promise<void> => {
    const bytes = await unterschriebenePdfBytes(f);
    await dateiAusgeben(new Blob([bytes], { type: 'application/pdf' }), vorankUnterschriebenDateiname(f));
};

/**
 * Digital unterschriebene Fassungen ohne abgelegte PDF (Plan §7): erzeugen,
 * in „Dokumente“ ablegen, `signiertSetzen`. Wirft nie; liefert die
 * Fehlersätze. Ein zweites Fenster trifft dieselbe `fassungRef` (gleiche
 * Zeile) und bekommt von `signiertSetzen` „bereits“ — doppelt liegt nichts.
 */
export const legeDigitaleUnterschriftenAb = async (
    projektId: string,
    fassungen: readonly VorankFassungZeile[],
): Promise<string[]> => {
    const fehler: string[] = [];
    for (const f of fassungen) {
        try {
            const unterschrift = vorankPdfUnterschrift(f);
            if (!unterschrift) continue;
            const datei = generateVorankuendigungPDF(f.inhalt, { unterschrift }).output('blob') as Blob;
            const dokumentId = await legeVorankDateiAb({ projectId: projektId, datei, ...vorankDigitalAblage(f) });
            await signiertSetzen(f.id, dokumentId);
        } catch (e) {
            console.error('[Vorankündigung] Ablage der digitalen Unterschrift', e);
            fehler.push(vorankDigitalAblageFehler(f, describeError(e)));
        }
    }
    return fehler;
};

// ── Papierweg: Datei einlesen (Plan §8) ─────────────────────────────────

/**
 * `art` statt eines booleschen `ok`: ohne `strictNullChecks` verengt
 * TypeScript einen booleschen Diskriminator nicht (Muster `ablageAbsage`).
 */
export type VorankEinleseErgebnis =
    | { art: 'ok'; bereits: boolean }
    /** `abgelegt`: Titel dessen, was vor dem Fehler schon in „Dokumente“ lag — es bleibt dort. */
    | { art: 'fehler'; fehler: string; abgelegt: string[] };

/**
 * Auf A4 bringen → Original ablegen (eine Datei: die Datei, wie sie kam;
 * mehrere: die normalisierte PDF gilt auch als Original, also EINE Zeile für
 * beide Verweise) → normalisierte PDF ablegen → `papierEingang`. Wirft nie.
 *
 * Bricht es nach einer Ablage ab, bleibt die abgelegte Datei in „Dokumente“
 * und wird im Ergebnis genannt. Ein zweiter Lauf mit derselben Eingabe trifft
 * über die `fassungRef` (`vorankEinleseSchluessel`) dieselben Zeilen und legt
 * nichts doppelt ab.
 */
export const liesUnterschriebeneDateiEin = async (a: {
    projektId: string;
    fassung: VorankFassungZeile;
    /** In der Reihenfolge des Dialogs, nur Dateien mit mitgenommenen Seiten (`vorankEinleseAuftrag`). */
    dateien: readonly File[];
    auswahl: VorankEinleseAuswahl;
    name: string;
    jetzt: string;
}): Promise<VorankEinleseErgebnis> => {
    const abgelegt: string[] = [];
    const name = (a.name || '').trim() || null;
    const schluessel = vorankEinleseSchluessel(a.fassung.id, a.dateien, a.auswahl);
    let schritt = 'Die Seiten ließen sich nicht auf A4 bringen';
    try {
        const pdf = await normalisiereAufA4(a.dateien, a.auswahl);
        const signiert = new Blob([pdf], { type: 'application/pdf' });
        const einzeln = a.dateien.length === 1 ? a.dateien[0] : null;

        let scanId: string | null = null;
        if (einzeln) {
            const original = vorankPapierAblage({
                fassung: a.fassung, art: 'original', originalName: einzeln.name, originalTyp: einzeln.type,
                schluessel, name, jetzt: a.jetzt,
            });
            schritt = 'Die Originaldatei ließ sich nicht in Dokumente ablegen';
            scanId = await legeVorankDateiAb({ projectId: a.projektId, datei: einzeln, ...original });
            abgelegt.push(original.titel);
        }

        const normalisiert = vorankPapierAblage({
            fassung: a.fassung, art: 'signiert', originalName: null, schluessel, name, jetzt: a.jetzt,
        });
        schritt = 'Die unterschriebene PDF ließ sich nicht in Dokumente ablegen';
        const signiertId = await legeVorankDateiAb({ projectId: a.projektId, datei: signiert, ...normalisiert });
        abgelegt.push(normalisiert.titel);

        schritt = 'Die Unterschrift ließ sich nicht an der Fassung vermerken';
        const ergebnis = await papierEingang({
            fassungId: a.fassung.id,
            scanDokumentId: scanId ?? signiertId,
            signiertDokumentId: signiertId,
            name,
        });
        return { art: 'ok', bereits: !!ergebnis?.bereits };
    } catch (e) {
        console.error('[Vorankündigung] Einlesen', e);
        return { art: 'fehler', fehler: `${schritt}: ${describeError(e)}`, abgelegt };
    }
};
